/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import "./App.scss";
import * as React from "react";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import ControlTray from "./components/control-tray/ControlTray";
import AudioPulse from "./components/audio-pulse/AudioPulse";
import { LiveClientOptions } from "./types";
import { useLiveAPIContext } from "./contexts/LiveAPIContext";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string") {
  throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
}

const apiOptions: LiveClientOptions = {
  apiKey: API_KEY,
};

function VoiceInterface() {
  const { connected, volume, client } = useLiveAPIContext();
  const [showGreeting, setShowGreeting] = React.useState(false);
  const greetingTimerRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    const onOpen = () => {
      setShowGreeting(false);
      greetingTimerRef.current = setTimeout(() => setShowGreeting(true), 1000);
    };
    const onContent = () => {
      if (greetingTimerRef.current) clearTimeout(greetingTimerRef.current);
      setShowGreeting(false);
    };
    client.on("open", onOpen).on("content", onContent);
    return () => {
      client.off("open", onOpen).off("content", onContent);
      if (greetingTimerRef.current) clearTimeout(greetingTimerRef.current);
    };
  }, [client]);

  return (
    <div className="voice-interface">
      <div className="status-container">
        <h1>Gemini Voice AI</h1>
        <p className="status-text">
          {connected ? (showGreeting ? "👋 Ready to help! Start speaking..." : "🟢 Connected - Listening...") : "⚪ Click play to start"}
        </p>
        <div className="audio-visualizer">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <LiveAPIProvider options={apiOptions}>
        <div className="streaming-console">
          <main>
            <div className="main-app-area">
              <VoiceInterface />
            </div>
            <ControlTray />
          </main>
        </div>
      </LiveAPIProvider>
    </div>
  );
}

export default App;
